"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TransformSchematic = exports.TRANSFORM_SCHEMATIC_DRAWING_ID_SYMBOL = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const TransformObject_1 = require("./TransformObject");
const TransformProject_1 = require("./TransformProject");
const TransformComponent_1 = require("./TransformComponent");
const TransformModule_1 = require("./TransformModule");
const TransformSchematicDrawing_1 = require("./TransformSchematicDrawing");
const TransformNetWire_1 = require("./TransformNetWire");
const TransformNetLabel_1 = require("./TransformNetLabel");
const TransformNetConnection_1 = require("./TransformNetConnection");
const TransformSchematicModelInstanceConfiguration_1 = require("./TransformSchematicModelInstanceConfiguration");
const TransformMultiInstanceDisplayModule_1 = require("./TransformMultiInstanceDisplayModule");
const TransformSchematicLayerSet_1 = require("./TransformSchematicLayerSet");
const Area_1 = require("../../../Area");
const TransformProcessingResult_1 = require("./TransformProcessingResult");
const TransformProjectLayerSet_1 = require("./TransformProjectLayerSet");
const mapDrawingNetLabels_1 = require("./mapDrawingNetLabels");
exports.TRANSFORM_SCHEMATIC_DRAWING_ID_SYMBOL = Symbol('TRANSFORM_SCHEMATIC_DRAWING_ID');
class TransformSchematic extends TransformObject_1.TransformObject {
    constructor(parent, args, netCombobulator, context) {
        super(parent, {
            ...args,
            modules: new Set(),
            drawings: new Set(),
            wires: new Set(),
            labels: new Set(),
            netConnections: new Set(),
            instanceConfigurations: new Set(),
            multiInstanceDisplayModules: new Set(),
            layerSets: new Set(),
        }, args.designId, new Set([
            TransformModule_1.TransformModule,
            TransformMultiInstanceDisplayModule_1.TransformMultiInstanceDisplayModule,
            TransformNetConnection_1.TransformNetConnection,
            TransformNetLabel_1.TransformNetLabel,
            TransformNetWire_1.TransformNetWire,
            TransformSchematicDrawing_1.TransformSchematicDrawing,
            TransformSchematicLayerSet_1.TransformSchematicLayerSet,
            TransformSchematicModelInstanceConfiguration_1.TransformSchematicModelInstanceConfiguration,
        ]), context);
        this.netCombobulator = netCombobulator;
        this._dependencies = [];
    }
    get designId() {
        return this._sharedData.designId;
    }
    get instance() {
        return this._sharedData.instance;
    }
    get hierarchyEntry() {
        return this._sharedData.instance.hierarchyEntry;
    }
    get sourceFile() {
        return this._sharedData.sourceFile;
    }
    add(child) {
        this.assertCanAdd(child);
        this._dependencies.push(child);
        if (child instanceof TransformModule_1.TransformModule) {
            this._sharedData.modules.add(child);
        }
        else if (child instanceof TransformSchematicDrawing_1.TransformSchematicDrawing) {
            this._sharedData.drawings.add(child);
        }
        else if (child instanceof TransformNetWire_1.TransformNetWire) {
            this._sharedData.wires.add(child);
        }
        else if (child instanceof TransformNetLabel_1.TransformNetLabel) {
            this._sharedData.labels.add(child);
        }
        else if (child instanceof TransformNetConnection_1.TransformNetConnection) {
            this._sharedData.netConnections.add(child);
        }
        else if (child instanceof TransformSchematicModelInstanceConfiguration_1.TransformSchematicModelInstanceConfiguration) {
            this._sharedData.instanceConfigurations.add(child);
        }
        else if (child instanceof TransformMultiInstanceDisplayModule_1.TransformMultiInstanceDisplayModule) {
            this._sharedData.multiInstanceDisplayModules.add(child);
        }
        else if (child instanceof TransformSchematicLayerSet_1.TransformSchematicLayerSet) {
            this._sharedData.layerSets.add(child);
            const layerSetIds = this.getLayerSetIds();
            if (layerSetIds.length > 1) {
                this._dependencies.push(new TransformProcessingResult_1.TransformProcessingResult(this, {
                    type: 'schematic-multiple-layer-sets',
                    message: `Multiple layer set ids found in schematic ${this._sharedData.designId}, will use first: ${layerSetIds.values().next().value}`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    foundLayerSetIds: Array.from(layerSetIds),
                }));
            }
            else {
                this._dependencies.push(TransformProjectLayerSet_1.TransformProjectLayerSet.From(this.requireParent(TransformProject_1.TransformProject), child));
            }
        }
    }
    compile() {
        const compiledModules = Array.from(this._sharedData.modules).map((child) => child.compiled);
        for (const wire of this._sharedData.wires) {
            void wire.compiled;
        }
        for (const label of this._sharedData.labels) {
            void label.compiled;
        }
        for (const conn of this._sharedData.netConnections) {
            void conn.compiled;
        }
        const compiledInstanceConfigurations = Array.from(this._sharedData.instanceConfigurations).map((child) => child.compiled);
        const compiledMultiInstanceDisplayModules = Array.from(this._sharedData.multiInstanceDisplayModules).map((child) => child.compiled);
        const layerSetIds = this.getLayerSetIds();
        const drawings = {};
        for (const transformDrawing of this._sharedData.drawings) {
            const { layers, drawing } = transformDrawing.compiled;
            for (const layer of layers) {
                const drawingWithId = {
                    ...drawing,
                    [exports.TRANSFORM_SCHEMATIC_DRAWING_ID_SYMBOL]: transformDrawing.deduplicationKey,
                };
                drawings[layer] ??= [];
                drawings[layer].push(drawingWithId);
            }
        }
        const netCombobulatorStart = performance.now();
        const netCbNets = this.netCombobulator.computeNets();
        const minConnections = this.context?.options?.minNetConnections;
        const netsMustHaveWireOrMultipleConnections = this.context?.options?.netsMustHaveWireOrMultipleConnections;
        const nets = Object.fromEntries(Array.from(netCbNets.values())
            .filter((net) => {
            if (minConnections !== undefined && net.connections.length < minConnections) {
                return false;
            }
            if (netsMustHaveWireOrMultipleConnections && net.wires.length === 0 && net.connections.length <= 1) {
                const hasAppliedNameOnConnection = net.connections.some(({ connection }) => connection.appliesNetName !== undefined);
                const hasNamingLabel = net.labels.some((label) => label.canNameNet);
                if (!hasAppliedNameOnConnection && !hasNamingLabel) {
                    return false;
                }
            }
            return true;
        })
            .map((net) => [
            net.name.valueOf(),
            {
                name: net.name.valueOf(),
                connections: net.connections.map(({ connection }) => ({
                    moduleRef: connection.moduleRef,
                    moduleName: connection.moduleName,
                    pinRef: connection.pinRef,
                })),
            },
        ]));
        const moduleByRef = new Map(compiledModules.map((m) => [m.ref, m]));
        const droppedNetNames = new Set();
        for (const net of netCbNets.values()) {
            if (!(net.name.valueOf() in nets)) {
                droppedNetNames.add(net.name.valueOf());
                continue;
            }
            for (const { connection } of net.connections) {
                const mod = moduleByRef.get(connection.moduleRef);
                if (mod) {
                    mod.pinNets[connection.pinRef] = net.name.valueOf();
                }
            }
        }
        if (droppedNetNames.size > 0) {
            for (const [layer, layerDrawings] of Object.entries(drawings)) {
                drawings[layer] = layerDrawings.map((drawing) => (0, mapDrawingNetLabels_1.mapDrawingNetLabels)(drawing, (net) => (droppedNetNames.has(net) ? null : undefined)));
            }
        }
        this._sharedData._combobulatorTimeMs = performance.now() - netCombobulatorStart;
        const model = {
            modelType: 'schematic',
            designId: this._sharedData.designId,
            designName: this._sharedData.designName,
            sourceFile: this._sharedData.sourceFile,
            sheetIndex: 0,
            layerSet: layerSetIds[0],
            grid: this._sharedData.grid,
            font: this._sharedData.font,
            drawings,
            edges_bbox: { minx: 0, miny: 0, maxx: 0, maxy: 0 },
            initial_zoombox: { minx: 0, miny: 0, maxx: 0, maxy: 0 },
            nets,
            modules: compiledModules,
            ports: {},
            metadata: this._sharedData.metadata,
            warnings: [],
        };
        this.computeBoundingBoxes(model);
        return model;
    }
    computeBoundingBoxes(model) {
        const components = {};
        for (const mod of this._sharedData.modules) {
            const dep = mod.dependencies[0];
            if (dep instanceof TransformComponent_1.TransformComponent) {
                const compiled = dep.compiled;
                if (!components[compiled.name]) {
                    components[compiled.name] = compiled;
                }
            }
        }
        const core = { components, warnings: [] };
        const projectLayers = Array.from(this._sharedData.layerSets).flatMap((child) => child.compiled.layers ?? []);
        const bbox = new Area_1.default();
        model.edges_bbox = bbox.extendFromSchModel(core, model, projectLayers, model.warnings, true).toModelBoundingBox();
        const visibleBbox = new Area_1.default();
        model.initial_zoombox = visibleBbox
            .extendFromSchModel(core, model, projectLayers, model.warnings, false)
            .toModelBoundingBox(model.edges_bbox);
        const MAX_ASPECT_RATIO = 2;
        model.edges_bbox = (0, sigma_engine_shared_1.padBboxToAspectRatio)(model.edges_bbox, MAX_ASPECT_RATIO);
        model.initial_zoombox = (0, sigma_engine_shared_1.padBboxToAspectRatio)(model.initial_zoombox, MAX_ASPECT_RATIO);
    }
    getLayerSetIds() {
        const layerSets = Array.from(this._sharedData.layerSets).map((child) => child.compiled);
        return Array.from(new Set(layerSets.map((layerSet) => layerSet.id)));
    }
}
exports.TransformSchematic = TransformSchematic;
//# sourceMappingURL=TransformSchematic.js.map